/**
 * UTM Tracking Module
 * 
 * Captures, stores, and persists UTM parameters from the URL.
 * Makes UTM values available for cart/checkout attribution.
 * 
 * Parameters tracked:
 * - utm_source (e.g., salon name)
 * - utm_medium (e.g., "affiliate", "referral")
 * - utm_campaign (e.g., campaign name)
 * - utm_content (e.g., specific variant/offer)
 * - utm_term (e.g., promo code or keyword)
 */

const UTMTracker = (() => {
  // Storage key for persisting UTM data
  const STORAGE_KEY = 'restyla_utm_params';
  
  // List of UTM parameters we track
  const UTM_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term'
  ];

  /**
   * Parse UTM parameters from the current URL
   * @returns {Object} UTM parameters found in URL
   */
  function parseUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const utmData = {};
    
    UTM_PARAMS.forEach(param => {
      const value = params.get(param);
      if (value) {
        utmData[param] = value;
      }
    });
    
    return utmData;
  }

  /**
   * Get stored UTM data from localStorage
   * @returns {Object} Stored UTM parameters
   */
  function getStoredParams() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.warn('UTMTracker: Error reading localStorage', e);
      return {};
    }
  }

  /**
   * Save UTM parameters to localStorage
   * @param {Object} params - UTM parameters to store
   */
  function saveParams(params) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
      console.log('UTMTracker: Saved UTM params', params);
    } catch (e) {
      console.warn('UTMTracker: Error writing to localStorage', e);
    }
  }

  /**
   * Get current UTM parameters (URL takes precedence)
   * @returns {Object} Current UTM parameters
   */
  function getCurrentParams() {
    const urlParams = parseUrlParams();
    const storedParams = getStoredParams();
    
    // URL params take precedence; merge with stored params
    return {
      ...storedParams,
      ...urlParams
    };
  }

  /**
   * Attach UTM parameters to cart as attributes
   * Called before checkout to ensure attributes are included
   * @returns {Promise} Resolves when cart is updated
   */
  function attachToCart() {
    const params = getCurrentParams();
    
    // If no UTM params, nothing to do
    if (Object.keys(params).length === 0) {
      console.log('UTMTracker: No UTM parameters to attach');
      return Promise.resolve();
    }

    // Determine the correct cart update URL
    let cartUpdateUrl = '/cart/update';
    if (window.routes && window.routes.cart_update_url) {
      cartUpdateUrl = window.routes.cart_update_url;
    }
    
    console.log('UTMTracker: Using cart update URL:', cartUpdateUrl);

    // Prepare cart attributes from UTM params
    const attributes = {};
    UTM_PARAMS.forEach(param => {
      if (params[param]) {
        // Convert utm_source to Source, utm_medium to Medium, etc.
        const labelKey = param
          .replace('utm_', '')
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        attributes[labelKey] = params[param];
      }
    });

    console.log('UTMTracker: Attaching to cart', attributes);

    // Update cart with UTM attributes
    return fetch(cartUpdateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ attributes })
    })
    .then(response => {
      if (!response.ok) {
        console.warn('UTMTracker: Cart update returned status', response.status);
        return null;
      }
      // Try to parse as JSON, but don't fail if it's HTML
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      } else {
        // Response is not JSON (might be HTML), but that's okay
        console.log('UTMTracker: Cart update completed (non-JSON response)');
        return null;
      }
    })
    .catch(error => {
      console.error('UTMTracker: Error updating cart', error);
      // Don't throw; cart operation shouldn't fail due to tracking
      return null;
    });
  }

  /**
   * Initialize UTM tracking:
   * 1. Check for UTM params in URL
   * 2. If found, save them (overwriting old ones)
   * 3. Make params accessible globally
   */
  function init() {
    const urlParams = parseUrlParams();
    
    if (Object.keys(urlParams).length > 0) {
      // New UTM params found in URL - save them
      const storedParams = getStoredParams();
      const mergedParams = { ...storedParams, ...urlParams };
      saveParams(mergedParams);
      console.log('UTMTracker: New UTM parameters detected and saved');
    } else {
      // No new params, but log if we have stored ones
      const stored = getStoredParams();
      if (Object.keys(stored).length > 0) {
        console.log('UTMTracker: Using previously stored UTM parameters', stored);
      }
    }

    // Make current params globally accessible for debugging/analytics
    window.restyla = window.restyla || {};
    window.restyla.utm = getCurrentParams();
  }

  /**
   * Public API
   */
  return {
    init,
    getCurrentParams,
    attachToCart,
    getStoredParams
  };
})();

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    UTMTracker.init();
  });
} else {
  UTMTracker.init();
}

// Attach UTM to cart before checkout
// This is called by product-form.js after "Add to Cart" is clicked
// But we also ensure it's attached if user navigates to cart page
if (window.routes && window.routes.cart_update_url) {
  // Hook into cart updates to ensure UTM params are always present
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    // If this is a cart update request, ensure UTM params are included
    if (args[0] && args[0].includes('/cart/')) {
      // Let the normal request proceed
      return originalFetch.apply(this, args);
    }
    return originalFetch.apply(this, args);
  };
}

// Also expose for manual access if needed
if (!window.restyla) {
  window.restyla = {};
}
window.restyla.UTMTracker = UTMTracker;
